import { useEffect, useRef } from 'react';

// Click-to-edit caption, drawn on the page above its photo. Carries the shared
// `ins-caption` class so the text it shows has exactly the PDF's typography,
// wrapping and reserved height — what you type is what prints. `ins-caption-edit`
// adds the editor-only affordances (dashed box, placeholder), which live in the
// editor stylesheet and so never reach the PDF.
export interface CaptionFieldProps {
  value: string;
  onChange: (value: string) => void;
}

export function CaptionField({ value, onChange }: CaptionFieldProps) {
  const ref = useRef<HTMLDivElement>(null);

  // The element owns its own text while focused — writing `value` back into it on
  // every keystroke would reset the caret to the start. So push the prop in only
  // when it disagrees and the user isn't typing here: that covers the sidebar
  // caption input, undo/redo, and switching pages.
  useEffect(() => {
    const el = ref.current;
    if (!el || el === document.activeElement) return;
    if (el.textContent !== value) el.textContent = value;
  }, [value]);

  return (
    <div
      ref={ref}
      className="ins-caption ins-caption-edit"
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label="Photo caption"
      // The slot below is a drag/drop and selection target; a click meant for the
      // caption shouldn't start a photo pan or clear the annotation selection.
      onMouseDown={(e) => e.stopPropagation()}
      onInput={(e) => onChange(e.currentTarget.textContent ?? '')}
      onKeyDown={(e) => {
        // A caption is a single plain string: Enter would put a <br> in it, so
        // treat Enter (and Escape) as "done" instead.
        if (e.key === 'Enter' || e.key === 'Escape') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      // Strip formatting/markup from pasted text — the caption stores text only.
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain').replace(/\s+/g, ' ');
        document.execCommand('insertText', false, text);
      }}
    />
  );
}
